<template>
  <div
    class="w-full"
    :class="[
      isAnimated && 'rounded-3xl transition-colors duration-700 lg:rounded-[56px]',
      active && isAnimated && 'bg-transparent',
      !active && isAnimated && 'bg-white'
    ]"
  >
    <button
      class="flex w-full cursor-pointer items-center justify-between rounded-3xl p-3 transition-colors lg:rounded-full lg:p-2"
      :class="[isAnimated ? 'bg-transparent' : 'bg-white']"
      :aria-controls="`acc-panel-${idx}`"
      :aria-expanded="active"
      @click="expand"
    >
      <div
        class="w-full text-left font-medium text-blue-800 transition-colors duration-300 hover:text-blue-200 lg:pl-10 lg:pr-2 lg:text-2xl"
      >
        <slot name="title" />
      </div>
      <div
        v-if="isAnimated"
        class="relative min-h-[48px] min-w-[48px] rounded-[100%] bg-light-gray bg-[url(/assets/images/backgrounds/star-gray.svg)] bg-cover lg:min-h-[96px] lg:min-w-[96px]"
      >
        <span
          class="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-gray-200 transition-all duration-500"
          :class="active ? 'h-[102%] w-[102%]' : 'h-[72%] w-[72%]'"
        />
        <Icon
          class="absolute left-1/2 top-1/2 z-2 -translate-x-1/2 -translate-y-1/2 transition-transform duration-300"
          name="chevron"
          color="#1D2B3D"
          :class="active && 'rotate-180'"
        />
      </div>

      <div
        v-else
        class="lg:font-grey-500 flex h-[48px] w-[48px] shrink-0 justify-center rounded-full bg-gray-200 align-middle text-2xl lg:h-[94px] lg:w-[94px]"
      >
        <Icon :size="isDesktop ? 'lg' : 'md'" :name="active ? 'minus' : 'plus'" />
      </div>
    </button>
    <Transition
      name="accordion"
      @enter="setHeight"
      @afterEnter="unsetHeight"
      @beforeLeave="setHeight"
      @afterLeave="unsetHeight"
    >
      <div v-show="active" :id="`acc-panel-${idx}`" :aria-hidden="!active">
        <div
          class="text-neutral-500 lg:pl-12 lg:text-2xl"
          :class="!isAnimated ? 'pt-6 lg:pt-8' : ''"
        >
          <slot name="content" />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
const { isDesktop } = useDevice()

export interface AccordionPanelProps {
  idx: number
  active: boolean
  isAnimated?: boolean
}

withDefaults(defineProps<AccordionPanelProps>(), {
  isAnimated: false
})
const emit = defineEmits(['expand'])

const isAnimating = ref(false)

const expand = () => {
  if (isAnimating.value) {
    return
  }
  emit('expand')
}

function setHeight(el: HTMLElement) {
  isAnimating.value = true
  el.style.height = el.scrollHeight + 'px'
}

function unsetHeight(el: HTMLElement) {
  isAnimating.value = false
  el.style.height = ''
}
</script>

<style lang="postcss">
.accordion-enter-active,
.accordion-leave-active {
  will-change: height, opacity;
  transition:
    height 300ms ease-out,
    opacity 300ms ease-out;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0 !important;
  opacity: 0;
}
</style>
